import React from 'react';
import { motion } from 'framer-motion';
import { staggerContainerVariants, fadeUpVariants } from './useScrollReveal';

type Work = {
  num: string;
  name: string;
  category: string;
  tags: string[];
  link: string | null;
  year: string;
  domain: string;
};

const works: Work[] = [
  {
    num: '01',
    name: 'Leo Lawn Service',
    category: 'Web Design + Branding',
    tags: ['Web Design', 'Branding', 'SEO'],
    link: 'https://leolawnserviceva.com',
    year: '2024',
    domain: 'leolawnserviceva.com',
  },
  {
    num: '02',
    name: 'Vasquez Brothers HVAC',
    category: 'Web Design + Branding',
    tags: ['Web Design', 'Rebranding'],
    link: 'https://www.vasquezbrothershvac.com/',
    year: '2024',
    domain: 'vasquezbrothershvac.com',
  },
  {
    num: '03',
    name: 'Stone Concrete Inc.',
    category: 'Web Design + Branding',
    tags: ['Web Design', 'Branding'],
    link: 'https://www.stoneconcreteinc.com/',
    year: '2025',
    domain: 'stoneconcreteinc.com',
  },
];

// Microlink free screenshot endpoint — cached on a global CDN for 24h.
const previewUrl = (url: string) =>
  `https://api.microlink.io/?url=${encodeURIComponent(
    url,
  )}&screenshot=true&meta=false&embed=screenshot.url&viewport.width=1280&viewport.height=800&waitUntil=networkidle0`;

const WorkCard: React.FC<{ work: Work }> = ({ work }) => {
  const content = (
    <div className="group relative h-full flex flex-col">
      {/* Top meta bar — sits ABOVE the preview, not over it */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <span className="font-syne text-[10px] text-[#888] font-bold tracking-widest">
          {work.num}
        </span>
        <span className="h-px flex-1 bg-[#1a1a1a]" />
        <span className="font-dm text-[10px] text-[#888] uppercase tracking-widest">
          {work.year}
        </span>
      </div>

      {/* Preview frame — the screenshot is the hero now */}
      <div className="relative overflow-hidden rounded-xl border border-[#1a1a1a] bg-[#0c0c0c] aspect-[4/3] mb-4">
        {/* Faux browser chrome */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 px-3 py-2 border-b border-[#1a1a1a] bg-[#0a0a0a]/95 backdrop-blur-sm">
          <div className="flex gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#2a2a2a]" />
            <span className="w-2 h-2 rounded-full bg-[#2a2a2a]" />
            <span className="w-2 h-2 rounded-full bg-[#2a2a2a]" />
          </div>
          <div className="flex-1 mx-2 h-4 rounded-sm bg-[#111] flex items-center px-2">
            <span className="font-dm text-[10px] text-[#888] truncate">{work.domain}</span>
          </div>
        </div>

        {/* Screenshot fills the whole card; pt-7 only offsets the chrome */}
        <div className="absolute inset-0 pt-7">
          {work.link ? (
            <img
              src={previewUrl(work.link)}
              alt={`${work.name} website preview`}
              loading="lazy"
              className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.04]"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full bg-[#111] flex items-center justify-center">
              <span className="font-syne text-[10px] text-[#444] uppercase tracking-widest">
                Coming Soon
              </span>
            </div>
          )}
        </div>

        {/* Hover arrow chip — top right, over the chrome */}
        <div className="absolute top-12 right-3 z-20">
          <motion.div
            className="w-8 h-8 rounded-full bg-black/70 backdrop-blur-sm border border-[#222] flex items-center justify-center text-[#aaa] group-hover:border-lime group-hover:text-lime group-hover:bg-black transition-all duration-300"
            whileHover={{ rotate: -45 }}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.div>
        </div>
      </div>

      {/* Title + meta sit BELOW the preview (cleaner, screenshot stays unobstructed) */}
      <div className="px-1">
        <h3 className="font-serif text-2xl md:text-[1.75rem] text-white leading-tight group-hover:text-lime transition-colors duration-300 mb-2">
          {work.name}
        </h3>
        <div className="flex items-center justify-between gap-3">
          <p className="font-dm text-[11px] text-[#888] uppercase tracking-widest truncate">
            {work.category}
          </p>
          <div className="flex gap-1.5 flex-shrink-0">
            {work.tags.slice(0, 2).map(t => (
              <span
                key={t}
                className="font-syne text-[10px] text-[#888] border border-[#2a2a2a] rounded-full px-2.5 py-1 group-hover:border-lime/30 group-hover:text-[#bbb] transition-all"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (work.link) {
    return (
      <a href={work.link} target="_blank" rel="noreferrer" className="block h-full">
        {content}
      </a>
    );
  }
  return <div className="h-full">{content}</div>;
};

const Works: React.FC = () => {
  return (
    <section id="works" className="py-32 bg-[#080808] border-t border-[#0f0f0f]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="section-label mb-5">Work That Makes Us Proud</p>
            <h2 className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] text-white leading-tight">
              Recent Works,{' '}
              <span className="italic text-lime">Notable Impact</span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <a href="#contact" className="btn-outline px-6 py-3 text-sm">Start a Project →</a>
          </motion.div>
        </div>

        {/* 3-card grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {works.map(w => (
            <motion.div key={w.name} variants={fadeUpVariants}>
              <WorkCard work={w} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Works;
